/**
 * El aviso de reloj corrido, para las cuatro estaciones (H6 de la ronda del plan; revisión
 * externa del 20/9/2026, hallazgo 17: hasta entonces solo lo tenía verde).
 *
 * `desvio_ms` lo mide el sync contra `hora_servidor` (y el enrolamiento); `ahoraMs()` ya lo
 * compensa, así que lo que el aviso previene es el reloj que salta entre syncs o la hora puesta a
 * mano: en producción, una parada fechada con `inicioDelTurno(ahoraMs(), turno)` cae en el turno
 * que no es. El aviso envuelve la vista a la que se iba, sea el inicio de la estación o «¿seguís
 * con…?» (Codex, 21/9: retomar un borrador con el reloj cambiado escribe horas corridas igual), y
 * sale una vez por sesión de la app: «seguir igual» lo acepta hasta que el desvío cambie más que el
 * umbral; «Ya la arreglé» recarga y vuelve a mirar.
 *
 * Ningún POC tiene esta pantalla: es de sistema, y el copy es nuestro.
 */

import { MINUTO_MS } from '../nucleo/tiempo';
import { app, type Vista } from './estado';

export const UMBRAL_RELOJ_MS = 5 * MINUTO_MS;

/** La vista pedida, o el aviso del reloj delante de ella si el desvío supera el umbral y no se aceptó ya. */
export function conAvisoDeReloj(luego: Vista): Vista {
	const desvio = app.preferencias.desvio_ms ?? 0;
	if (Math.abs(desvio) <= UMBRAL_RELOJ_MS) return luego;
	const aceptado = app.relojAceptadoMs;
	if (aceptado !== null && Math.abs(desvio - aceptado) <= UMBRAL_RELOJ_MS) return luego;
	return { tipo: 'advertencia-reloj', desvioMinutos: Math.round(Math.abs(desvio) / MINUTO_MS), luego };
}

/** «Seguir igual»: el desvío de ahora queda aceptado por esta sesión. */
export function aceptarReloj(): void {
	app.relojAceptadoMs = app.preferencias.desvio_ms ?? 0;
}

/** Qué se arruina con la hora mal, según la estación del teléfono, y cómo se dice «seguir igual». */
export function textoDelAviso(): { queSeArruina: string; seguir: string } {
	switch (app.sesion?.estacion) {
		case 'verde':
			return { queSeArruina: 'Los viajes van a quedar con la hora equivocada.', seguir: 'Cargar igual' };
		case 'produccion':
			return {
				queSeArruina: 'Los silos, las pesadas y las paradas van a quedar con la hora equivocada, y una parada puede caer en el turno que no es.',
				seguir: 'Seguir igual',
			};
		case 'calidad':
			return { queSeArruina: 'Las muestras van a quedar con la hora equivocada.', seguir: 'Seguir igual' };
		case 'envasado':
			return { queSeArruina: 'Los pallets y las cargas van a quedar con la hora equivocada.', seguir: 'Seguir igual' };
		default:
			return { queSeArruina: 'Lo que cargues va a quedar con la hora equivocada.', seguir: 'Seguir igual' };
	}
}
